import { reflect } from '@effector/reflect';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { WalletIcon } from 'lucide-react';
import { not } from 'patronum';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { shiftModel } from 'entities/shift';
import { $t, breakpoints, translated } from 'shared/lib';
import { Calculator, createModalForm, ModalFormProps } from 'shared/ui';
import { WithdrawPlayerModel } from '../../model';

export const createWithdrawPlayerModal = (model: WithdrawPlayerModel) => {
	const ModalForm = createModalForm(model);

	const AmountInput = reflect({
		view: Calculator,
		bind: {
			'data-test-id': 'withdraw-player-amount-input',
			onChange: (e) => model.form.fields.amount.change(e.toString()),
			size: breakpoints.md.map((md) => (md ? 'md' : 'sm')),
			value: model.form.fields.amount.$value,
			numberInputProps: combine({
				placeholder: '250.00',
				label: translated('withdraw-player.amount'),
				rightSection: shiftModel.$shiftTotal.map((shift) => shift?.currency ?? null),
				errorProps: { 'data-test-id': 'withdraw-player-amount-input-error' },
				error: combine(
					[model.form.fields.amount.$errors, $t],
					([errors, t]) =>
						errors.length > 0 &&
						t(`errors:validation.${errors[0]}`, {
							fieldName: t('withdraw-player.amount'),
						}),
				),
			}),
		},
	});

	const WithdrawPlayerModal = forwardRef<HTMLDivElement, ModalFormProps>(({ ...props }, ref) => {
		const { t } = useTranslation();

		const [pending, md] = useUnit([model.$pending, breakpoints.md]);

		return (
			<ModalForm
				ref={ref}
				title={t('withdraw-player.title')}
				submitButtonProps={{
					children: t('withdraw-player.withdraw'),
					leftSection: <WalletIcon />,
					loading: pending,
					fullWidth: true,
					size: md ? 'xl' : 'lg',
				}}
				size={md ? '2xl' : 'xl'}
				{...props}
			>
				<AmountInput />
			</ModalForm>
		);
	});

	const WithdrawPlayerModalButton = reflect({
		view: ModalForm.Button,
		bind: {
			'data-test-id': 'withdraw-player-button',
			leftSection: <WalletIcon />,
			children: translated('withdraw-player.withdraw'),
			size: breakpoints.md.map((md) => (md ? 'lg' : 'md')),
			fullWidth: not(breakpoints.sm),
		},
	});

	return Object.assign(WithdrawPlayerModal, {
		Button: WithdrawPlayerModalButton,
	});
};
